import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { VaultService } from '../services/vault.service';
import * as VaultActions from './vault.actions';

@Injectable()
export class VaultEffects {
  private readonly translate = inject(TranslateService);

  createVault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.createVault),
      switchMap(action =>
        this.vaultService.createVault({ name: action.name }).pipe(
          map(response =>
            VaultActions.createVaultSuccess({
              message: response.message,
            })
          ),
          catchError(error =>
            of(
              VaultActions.createVaultFailure({
                error:
                  error.message ||
                  this.translate.instant('assets.vaults.errors.createFailed'),
              })
            )
          )
        )
      )
    )
  );

  // Note: the success toast is now driven by `apiResponseInterceptor`
  // (server-localized message). This effect only handles navigation.
  createVaultSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VaultActions.createVaultSuccess),
        tap(() => {
          this.router.navigate(['/dashboard/vaults']);
        })
      ),
    { dispatch: false }
  );

  updateVault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.updateVault),
      switchMap(action =>
        this.vaultService
          .updateVault(action.vaultId, { name: action.name })
          .pipe(
            map(response =>
              VaultActions.updateVaultSuccess({
                message: response.message,
              })
            ),
            catchError(error =>
              of(
                VaultActions.updateVaultFailure({
                  error:
                    error.message ||
                    this.translate.instant('assets.vaults.errors.updateFailed'),
                })
              )
            )
          )
      )
    )
  );

  // The success toast for `updateVault` is now driven by
  // `apiResponseInterceptor` — no per-effect side-effect needed.

  loadVaultDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.loadVaultDetail),
      switchMap(({ vaultId }) =>
        this.vaultService.getVaultDetail(vaultId).pipe(
          map(response =>
            VaultActions.loadVaultDetailSuccess({ detail: response.data })
          ),
          catchError(error =>
            of(
              VaultActions.loadVaultDetailFailure({
                error:
                  error?.message ||
                  this.translate.instant(
                    'assets.vaults.errors.loadDetailFailed'
                  ),
              })
            )
          )
        )
      )
    )
  );

  loadBalanceDistribution$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.loadBalanceDistribution),
      switchMap(({ vaultId }) =>
        this.vaultService.getBalanceDistribution(vaultId).pipe(
          map(response =>
            VaultActions.loadBalanceDistributionSuccess({
              data: response.data,
            })
          ),
          catchError(error =>
            of(
              VaultActions.loadBalanceDistributionFailure({
                error:
                  error?.message ||
                  this.translate.instant(
                    'assets.vaults.errors.loadBalanceDistributionFailed'
                  ),
              })
            )
          )
        )
      )
    )
  );

  loadRecentActivities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.loadRecentActivities),
      switchMap(({ vaultId, page, limit }) =>
        this.vaultService.getRecentActivities(vaultId, page, limit).pipe(
          map(response =>
            VaultActions.loadRecentActivitiesSuccess({
              activities: response.data,
              pagination: response.pagination,
            })
          ),
          catchError(error =>
            of(
              VaultActions.loadRecentActivitiesFailure({
                error:
                  error?.message ||
                  this.translate.instant(
                    'assets.vaults.errors.loadRecentActivitiesFailed'
                  ),
              })
            )
          )
        )
      )
    )
  );

  loadVaultWhitelistAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.loadVaultWhitelistAssignments),
      switchMap(({ vaultId, page, limit }) =>
        this.vaultService.getVaultWhitelistAddresses(vaultId, page, limit).pipe(
          map(response => {
            const all = response.data?.whitelistAssignments?.slice() ?? [];
            const pagination = response.pagination;

            if (pagination) {
              return VaultActions.loadVaultWhitelistAssignmentsSuccess({
                assignments: all,
                pagination,
              });
            }

            const start = (page - 1) * limit;
            const assignments = all.slice(start, start + limit);
            return VaultActions.loadVaultWhitelistAssignmentsSuccess({
              assignments,
              pagination: {
                page,
                limit,
                total: all.length,
                totalPages: Math.max(1, Math.ceil(all.length / limit)),
              },
            });
          }),
          catchError(error =>
            of(
              VaultActions.loadVaultWhitelistAssignmentsFailure({
                error:
                  error?.message ||
                  this.translate.instant(
                    'assets.vaults.errors.loadWhitelistFailed'
                  ),
              })
            )
          )
        )
      )
    )
  );

  disableVault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.disableVault),
      switchMap(({ vaultId }) =>
        this.vaultService.disableVault(vaultId).pipe(
          map(response =>
            VaultActions.disableVaultSuccess({
              message: response.message,
              requestId: response.data?.requestId ?? '',
              vaultId,
            })
          ),
          catchError(error =>
            of(
              VaultActions.disableVaultFailure({
                error:
                  error?.message ||
                  this.translate.instant('assets.vaults.errors.disableFailed'),
              })
            )
          )
        )
      )
    )
  );

  enableVault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.enableVault),
      switchMap(({ vaultId }) =>
        this.vaultService.enableVault(vaultId).pipe(
          map(response =>
            VaultActions.enableVaultSuccess({
              message: response.message,
              requestId: response.data?.requestId ?? '',
              vaultId,
            })
          ),
          catchError(error =>
            of(
              VaultActions.enableVaultFailure({
                error:
                  error?.message ||
                  this.translate.instant('assets.vaults.errors.enableFailed'),
              })
            )
          )
        )
      )
    )
  );

  refetchVaultDetailAfterDisableSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.disableVaultSuccess),
      delay(1500),
      map(({ vaultId }) => VaultActions.loadVaultDetail({ vaultId }))
    )
  );

  refetchVaultDetailAfterEnableSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaultActions.enableVaultSuccess),
      delay(1500),
      map(({ vaultId }) => VaultActions.loadVaultDetail({ vaultId }))
    )
  );

  constructor(
    private actions$: Actions,
    private vaultService: VaultService,
    private router: Router
  ) {}
}
